// src/core/helpers/createParserOptions.tsx
import React from 'react';
import { DOMNode, Element, domToReact, HTMLReactParserOptions } from 'html-react-parser';
import ControlledVideo from '../../ui/components/tools/ControlledVideo';
import MediaPreview from '../../ui/components/imgs/MediaPreview';
import { optimizeYouTubeUrl } from '../../ui/components/dialogs/FullscreenDialog.utils';
import { isRaspberryPi } from '../../core/utils/deviceDetection';

export const createParserOptions = ({
  title,
  showMedia,
  showExpanded,
  showFullScreen,
  useRowLayout,
  cleanHtml,
}: {
  title: string;
  showMedia: boolean;
  showExpanded: boolean | undefined;
  showFullScreen: boolean | undefined;
  useRowLayout: boolean;
  cleanHtml?: string | null;
}): HTMLReactParserOptions => {
  const opts: HTMLReactParserOptions = {
    replace: (node: DOMNode) => {
      // Preservar nodos de texto que contengan saltos de línea
      if (node.type === 'text') {
        const text = node.data || '';
        if (!text.trim() && !text.includes('\n')) return null;
        // Si el texto tiene saltos de línea, simplemente parsearlos normalmente
        // El whiteSpace: 'pre-line' en el contenedor se encargará del resto
        return undefined;
      }
      if (node.type !== 'tag') return undefined;
      const el = node as Element;
      const tag = el.name.toLowerCase();

      // Eliminar <p>/<div> vacíos
      if (
        (tag === 'p' || tag === 'div') &&
        !el.children.some((c) => {
          return c.type === 'tag' || (c.type === 'text' && (c as any).data.trim() !== '');
        })
      ) {
        return null;
      }

      const effectiveShowMedia = showMedia || showExpanded || showFullScreen;

      // variante B: background-image (cover)
      if (tag === 'img' && effectiveShowMedia) {
        const src =
          el.attribs.src ||
          el.attribs['data-src'] ||
          (el.attribs.srcset ? el.attribs.srcset.split(',')[0]?.trim().split(' ')[0] : '') ||
          el.attribs['data-srcset'] ||
          '';
        if (!src) return null;

        const wrapperClass = useRowLayout
          ? 'w-[40%] pr-6 flex-shrink-0'
          : showExpanded
            ? 'w-[100%] h-[80vh] relative flex items-center justify-center'
            : 'w-full mb-4 flex-shrink-0';

        return (
          <div className={wrapperClass}>
            <MediaPreview src={src} showExpanded={showExpanded} />
            {showExpanded && cleanHtml && (
              <div className="absolute top-4 right-4 z-[99] w-1/3 h-auto flex items-start justify-end pr-2">
                <div className="bg-black bg-opacity-50 p-4 rounded-md">
                  <div className="text-white text-left font-bold tv-text-base [&_span]:tv-text-lg">
                    {/* eliminar el texto sobre la imagen de en este momento */}
                    {/* {parse(cleanHtml)} */}
                    {title}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      // IFRAME
      if (tag === 'iframe') {
        const raw = el.attribs.src || el.attribs['data-src'] || '';
        if (!raw) return null;
        const normalized = raw.startsWith('http')
          ? raw
          : raw.startsWith('//')
            ? 'https:' + raw
            : raw;

        // Detectar si es Raspberry Pi para aplicar optimizaciones más agresivas
        const isRPi = isRaspberryPi();

        // Usar optimizeYouTubeUrl para aplicar todas las optimizaciones
        const srcWithParams = optimizeYouTubeUrl(normalized, isRPi);

        // wrapper dentro del flujo: en móvil ocupa 100% y en md+ la mitad
        const wrapperClass = useRowLayout
          ? 'w-[40%] pr-6 flex-shrink-0'
          : showExpanded
            ? 'w-full h-[80vh] relative flex items-center justify-center'
            : 'w-full mb-4 flex-shrink-0';

        const iframeClass = showExpanded
          ? 'w-full h-[60vh] max-h-[80vh] rounded-sm'
          : 'w-full aspect-video rounded-lg overflow-hidden';

        return (
          <div className={wrapperClass}>
            <iframe
              src={srcWithParams}
              title={title || 'Embedded media'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; loop"
              allowFullScreen
              loading="lazy"
              className={iframeClass}
              style={{
                transform: 'translateZ(0)',
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            />
          </div>
        );
      }

      // VIDEO
      if (tag === 'video') {
        const src = el.attribs.src || el.attribs['data-src'] || '';
        const poster = el.attribs.poster;
        if (!src) return null;
        const wrapperClass = useRowLayout
          ? 'w-full h-[30vh] md:w-1/2 p-2 flex items-center justify-center'
          : 'w-full p-2';
        const videoClass = showExpanded
          ? 'w-full h-[60vh] max-h-[80vh]'
          : 'w-full aspect-video rounded-lg overflow-hidden';

        return (
          <div className={wrapperClass}>
            <div className={videoClass}>
              <ControlledVideo src={src} playing poster={poster} />
            </div>
          </div>
        );
      }

      // Filtrar children si showMedia === false
      const filteredChildren = el.children.filter(
        (c) => !(c.type === 'tag' && !showMedia && (c as Element).name.toLowerCase() === 'img'),
      );
      const children = domToReact(filteredChildren as DOMNode[], opts);

      const base = 'm-0 p-0';
      switch (tag) {
        case 'p':
          return (
            <p className={`tv-text-base leading-tight text-white font-bold ${base} mb-0`}>
              {children}
            </p>
          );

        case 'ul':
          return (
            <ul className={`list-disc pl-5 ${base} grid grid-cols-1 gap-y-0`}>
              {children}
            </ul>
          );

        case 'ol':
          return (
            <ol className={`list-decimal pl-5 ${base} grid grid-cols-1 gap-y-0`}>
              {children}
            </ol>
          );

        case 'li':
          return (
            <li className={`tv-text-sm ${base}`}>
              {children}
            </li>
          );

        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6': {
          return (
            <p className={`tv-text-base leading-tight text-white font-bold ${base} mb-0 mt-0.5`}>
              {children}
            </p>
          );
        }
        case 'strong':
        case 'b':
          return <strong className="font-bold">{children}</strong>;
        case 'em':
        case 'i':
          return <em className="italic">{children}</em>;
        case 'div': {
          return (
            <div className={`tv-text-base leading-tight text-white ${base}`}>
              {children}
            </div>
          );
        }
        case 'span':
        case 'font': {
          return <>{children}</>;
        }
        case 'br':
          return <br />;
        default:
          return <>{children}</>;
      }

    },
  };

  return opts;
};
